// src/pages/InnovaFast.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Rocket, Send, Loader2, CheckCircle } from 'lucide-react';
import { usePreventZoom } from '../hooks/usePreventZoom';
import InnovaFastLobby from '../components/InnovaFastLobby';
import { getSafeToken } from '../utils/tokenHelper';

// Helper function para obtener la URL del backend con fallback robusto
const getBackendUrl = () => {
  const envUrl = import.meta.env.VITE_BACKEND_URL;
  // Si la variable de entorno no está definida, usar la URL de producción
  return envUrl && envUrl !== 'undefined' ? envUrl : 'https://edutechpulselab.com/api';
};

const InnovaFast = () => {
  const { user } = useAuth();
  
  // Hook para prevenir zoom automático en móviles
  usePreventZoom();
  
  const [sessionId, setSessionId] = useState(null);
  const [currentStage, setCurrentStage] = useState('Descubrimiento');
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [completedStages, setCompletedStages] = useState([]);
  const [aiTyping, setAiTyping] = useState(false);
  const [showLobby, setShowLobby] = useState(true);
  const [isExistingSession, setIsExistingSession] = useState(false);
  
  // Ref para el auto-scroll
  const messagesEndRef = useRef(null);

  // Verificar si hay un chatId en la URL para cargar sesión existente
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const existingChatId = urlParams.get('chatId');
    
    if (existingChatId) {
      setShowLobby(false);
      loadExistingSession(existingChatId);
    }
  }, []);

  // Función para cargar una sesión existente
  const loadExistingSession = async (chatIdFromUrl) => {
    try {
      console.log('📂 loadExistingSession iniciado con chatId:', chatIdFromUrl);
      setLoading(true);
      const token = getSafeToken();
      
      const response = await fetch(`${getBackendUrl()}/innovafast/sessions/${chatIdFromUrl}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        const session = data.session;
        setSessionId(chatIdFromUrl); // Usar chatId como sessionId interno
        setCurrentStage(session.stage);
        
        // Convertir mensajes al formato esperado por el frontend
        // Priorizar mensajes sobre history si ambos existen
        const messages = session.mensajes || session.history || [];
        const formattedMessages = messages.map((msg, index) => ({
          id: Date.now() + index,
          sender: msg.sender,
          content: msg.content,
          timestamp: new Date(msg.fechaEnvio || Date.now())
        }));
        
        setMessages(formattedMessages);
        setSessionStarted(true);
        setIsExistingSession(true);
        console.log('✅ Estados configurados - sessionStarted: true, isExistingSession: true, messages:', formattedMessages.length);
        // Limpiar la URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        console.error('Error al cargar sesión existente:', data);
      }
    } catch (error) {
      console.error('Error al cargar sesión InnovaFast:', error);
    } finally {
      setLoading(false);
    }
  };

  const stages = [
    { id: 'Descubrimiento', name: 'Descubrimiento', description: 'Identifica el problema o oportunidad', color: 'emerald' },
    { id: 'Definición', name: 'Definición', description: 'Define el desafío específico', color: 'blue' },
    { id: 'Ideación', name: 'Ideación', description: 'Genera soluciones creativas', color: 'purple' },
    { id: 'Prototipado', name: 'Prototipado', description: 'Desarrolla prototipos viables', color: 'orange' },
    { id: 'Validación', name: 'Validación', description: 'Valida tu solución con usuarios', color: 'red' }
  ];

  const getCurrentStageIndex = () => stages.findIndex(stage => stage.id === currentStage);
  const getStageStatus = (stageIndex) => {
    const currentIndex = getCurrentStageIndex();
    if (stageIndex < currentIndex) return 'completed';
    if (stageIndex === currentIndex) return 'current';
    return 'upcoming';
  };

  // Función para hacer scroll automático al último mensaje
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Auto-scroll cuando se agregan nuevos mensajes o cuando la IA está escribiendo
  useEffect(() => {
    scrollToBottom();
  }, [messages, aiTyping]);

  // Funciones para el lobby
  const handleStartNew = () => {
    setShowLobby(false);
    setIsExistingSession(false);
    startSession();
  };

  const handleResumeSession = (sessionId) => {
    console.log('🔄 handleResumeSession iniciado con sessionId:', sessionId);
    setShowLobby(false);
    setIsExistingSession(true);
    loadExistingSession(sessionId);
  };

  const handleBackToLobby = () => {
    setShowLobby(true);
    setSessionStarted(false);
    setMessages([]);
    setSessionId(null);
    setCurrentStage('Descubrimiento');
    setIsExistingSession(false);
    // Limpiar URL
    const url = new URL(window.location);
    url.searchParams.delete('chatId');
    window.history.replaceState({}, '', url);
  };

  // Iniciar sesión al montar el componente
  const startSession = async () => {
    try {
      setLoading(true);
      const token = getSafeToken();
      
      const response = await fetch(`${getBackendUrl()}/innovafast/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sessionName: `Sesión InnovaFast - ${new Date().toLocaleDateString()}`
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSessionId(data.sessionId);
        setCurrentStage(data.stage);
        setMessages([{
          id: Date.now(),
          sender: 'ai',
          content: data.question,
          timestamp: new Date()
        }]);
        setSessionStarted(true);
      } else {
        console.error('Error al iniciar sesión:', data);
      }
    } catch (error) {
      console.error('Error al iniciar sesión InnovaFast:', error);
    } finally {
      setLoading(false);
    }
  };

  // Procesar entrada del usuario
  const processUserInput = async () => {
    if (!userInput.trim() || !sessionId || loading) return;

    const userMessage = {
      id: Date.now(),
      sender: 'user', 
      content: userInput.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setUserInput('');
    setLoading(true);
    setAiTyping(true); // Mostrar indicador de que la IA está escribiendo

    try {
      const token = getSafeToken();
      
      const response = await fetch(`${getBackendUrl()}/innovafast/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sessionId: sessionId,
          user_input: userInput.trim()
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setCurrentStage(data.stage);
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          sender: 'ai',
          content: data.question,
          timestamp: new Date()
        }]);
      } else {
        console.error('Error al procesar entrada:', data);
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          sender: 'ai', 
          content: 'Lo siento, hubo un error procesando tu respuesta. ¿Podrías intentar de nuevo?',
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      console.error('Error al procesar entrada:', error);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        sender: 'ai',
        content: 'Hubo un problema de conexión. Por favor, intenta nuevamente.',
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
      setAiTyping(false); // Ocultar indicador cuando termine
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      processUserInput();
    }
  };

  // Si mostramos el lobby, renderizar solo eso
  if (showLobby) {
    return (
      <InnovaFastLobby 
        onStartNew={handleStartNew}
        onResumeSession={handleResumeSession}
      />
    );
  }

  return (
    <div className="p-2 md:p-6 bg-gradient-to-br from-emerald-50 to-green-100 h-full flex flex-col overflow-y-auto pb-24">
      {/* Header - Compacto en mobile */}
      <div className="mb-2 md:mb-6 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3 mb-1 md:mb-2">
            <Rocket size={20} className="text-emerald-600 md:w-8 md:h-8" />
            <div>
              <h1 className="text-xl font-semibold text-gray-800">INNOVAFAST</h1>
              <p className="text-xs md:text-base text-gray-600 hidden sm:block">Acelera tu proceso de innovación</p>
            </div>
          </div>
          <button
            onClick={handleBackToLobby}
            className="text-emerald-600 hover:text-emerald-700 text-sm md:text-base font-medium px-3 py-1 rounded-lg hover:bg-emerald-50 transition-colors"
          >
            ← Mis Proyectos
          </button>
        </div>
        {sessionStarted && (
          <div className="mt-4 p-3 bg-emerald-100 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-emerald-800">Etapa actual:</span>
              <span className="text-sm font-bold text-emerald-900">{currentStage}</span>
            </div>
            <div className="mt-1 text-xs text-emerald-700">
              Etapa {getCurrentStageIndex() + 1} de {stages.length}
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex flex-col">
        
        {/* Bienvenida */}
        {!sessionStarted && (
          <div className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-6 text-center">
            <Rocket size={40} className="mx-auto text-emerald-600 mb-4 md:w-12 md:h-12" />
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">
              ¡Bienvenido a InnovaFast, {user?.nombre || 'usuario'}!
            </h2>
            <p className="text-gray-600 mb-6 text-sm md:text-base">
              Te guiaré a través de un proceso estructurado de 5 etapas para acelerar tu innovación:
            </p>
            {/* Indicador de progreso mejorado - Optimizado para móvil */}
            <div className="mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
                <h3 className="text-base font-medium text-gray-700 mb-2 sm:mb-0">Proceso InnovaFast</h3>
                <span className="text-sm text-gray-500 text-center sm:text-right">Etapa 1 de 5</span>
              </div>
              
              {/* Barra de progreso - Vista escritorio */}
              <div className="hidden md:flex items-center mb-6">
                {stages.map((stage, index) => (
                  <React.Fragment key={stage.id}>
                    <div className="flex flex-col items-center">
                      <div 
                        className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                          getStageStatus(index) === 'completed' 
                            ? 'bg-emerald-500 text-white' 
                            : getStageStatus(index) === 'current'
                            ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-300'
                            : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        {getStageStatus(index) === 'completed' ? (
                          <CheckCircle size={16} />
                        ) : (
                          index + 1
                        )}
                      </div>
                      <span className={`text-xs mt-2 text-center max-w-16 ${
                        getStageStatus(index) === 'current' ? 'text-emerald-700 font-semibold' : 'text-gray-500'
                      }`}>
                        {stage.name.split(' ').map((word, i) => (
                          <span key={i} className="block">{word}</span>
                        ))}
                      </span>
                    </div>
                    {index < stages.length - 1 && (
                      <div 
                        className={`flex-1 h-1 mx-3 transition-all duration-300 ${
                          getStageStatus(index) === 'completed' ? 'bg-emerald-300' : 'bg-gray-200'
                        }`}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Barra de progreso simplificada para móvil */}
              <div className="md:hidden mb-6">
                <div className="flex items-center justify-center space-x-2 mb-4">
                  {stages.map((stage, index) => (
                    <div 
                      key={stage.id}
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300 ${
                        getStageStatus(index) === 'completed' 
                          ? 'bg-emerald-500 text-white' 
                          : getStageStatus(index) === 'current'
                          ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-300'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {getStageStatus(index) === 'completed' ? (
                        <CheckCircle size={12} />
                      ) : (
                        index + 1
                      )}
                    </div>
                  ))}
                </div>
                {/* Indicador de progreso lineal */}
                <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                  <div 
                    className="bg-emerald-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${(getCurrentStageIndex() + 1) * 20}%` }}
                  ></div>
                </div>
              </div>

              {/* Tarjetas de etapas - Optimizadas para móvil */}
              <div className="space-y-3 md:space-y-0 md:grid md:grid-cols-5 md:gap-3">
                {stages.map((stage, index) => (
                  <div 
                    key={stage.id}
                    className={`p-4 md:p-3 rounded-lg border transition-all duration-300 ${
                      getStageStatus(index) === 'completed' 
                        ? 'bg-emerald-50 border-emerald-200' 
                        : getStageStatus(index) === 'current'
                        ? 'bg-emerald-100 border-emerald-300 shadow-sm'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className={`text-sm md:text-xs font-semibold mb-1 ${
                      getStageStatus(index) === 'completed' || getStageStatus(index) === 'current'
                        ? 'text-emerald-800' 
                        : 'text-gray-600'
                    }`}>
                      {index + 1}. {stage.name}
                    </div>
                    <div className={`text-sm md:text-xs ${
                      getStageStatus(index) === 'completed' || getStageStatus(index) === 'current'
                        ? 'text-emerald-600' 
                        : 'text-gray-500'
                    }`}>
                      {stage.description}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={startSession}
              disabled={loading}
              className="bg-emerald-600 text-white px-6 md:px-8 py-3 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto text-sm md:text-base w-full max-w-xs md:max-w-none md:w-auto"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Iniciando...
                </>
              ) : (
                <>
                  <Rocket size={18} />
                  {isExistingSession ? 'Retomar Sesión InnovaFast' : 'Iniciar Sesión InnovaFast'}
                </>
              )}
            </button>
          </div>
        )}

        {/* Indicador de progreso compacto para sesión activa */}
        {sessionStarted && (
          <div className="bg-white rounded-lg shadow-sm p-2 md:p-4 mb-2 md:mb-4 flex-shrink-0">
            <div className="flex items-center justify-between mb-2 md:mb-3">
              <h4 className="text-sm md:text-base font-semibold text-gray-800">Progreso</h4>
              <span className="text-xs md:text-sm text-gray-500">
                {getCurrentStageIndex() + 1}/{stages.length}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              {stages.map((stage, index) => (
                <div key={stage.id} className="flex-1">
                  <div 
                    className={`h-2 rounded-full transition-all duration-500 ${
                      getStageStatus(index) === 'completed' 
                        ? 'bg-emerald-500' 
                        : getStageStatus(index) === 'current'
                        ? 'bg-emerald-300'
                        : 'bg-gray-200'
                    }`}
                  />
                  <span className={`text-xs mt-1 block text-center ${
                    getStageStatus(index) === 'current' ? 'text-emerald-700 font-semibold' : 'text-gray-500'
                  }`}>
                    {stage.name.split(' ')[0]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chat Messages - Optimizado para mobile */}
        {sessionStarted && (
          <div className="flex-1 bg-white rounded-lg shadow-sm mb-2 md:mb-4 flex flex-col min-h-0">
            <div className="flex-1 p-2 md:p-4 overflow-y-auto space-y-2 md:space-y-4 min-h-0">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'} px-1`}>
                  <div className={`max-w-[85%] md:max-w-xs lg:max-w-md px-3 md:px-4 py-2 rounded-2xl md:rounded-lg ${
                    msg.sender === 'user' 
                      ? 'bg-emerald-600 text-white rounded-br-md' 
                      : 'bg-gray-100 text-gray-800 rounded-bl-md'
                  }`}>
                    <p className="text-sm leading-relaxed">{msg.content}</p>
                    <p className={`text-xs mt-1 ${
                      msg.sender === 'user' ? 'text-emerald-200' : 'text-gray-400'
                    }`}>
                      {msg.timestamp.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
              {/* Indicador de IA escribiendo */}
              {aiTyping && (
                <div className="flex justify-start px-1">
                  <div className="bg-gradient-to-r from-gray-100 to-gray-50 text-gray-700 px-3 md:px-4 py-3 rounded-2xl md:rounded-lg flex items-center gap-3 shadow-sm border">
                    <div className="flex space-x-1 items-center">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full ai-typing-dot"></div>
                      <div className="w-2 h-2 bg-emerald-500 rounded-full ai-typing-dot"></div>
                      <div className="w-2 h-2 bg-emerald-500 rounded-full ai-typing-dot"></div>
                    </div>
                    <span className="text-sm text-gray-600 font-medium">InnovaFast está analizando...</span>
                  </div>
                </div>
              )}
              
              {/* Elemento invisible para el auto-scroll */}
              <div ref={messagesEndRef} />
            </div>
            
            {/* Input Area - Optimizado para mobile */}
            <div className="border-t p-2 md:p-4 bg-gray-50 md:bg-white">
              <div className="flex gap-2 items-end">
                <textarea
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Tu respuesta..."
                  className="flex-1 border border-gray-200 rounded-2xl md:rounded-lg px-4 py-2 resize-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white shadow-sm text-base min-h-[40px] max-h-24"
                  rows={1}
                  disabled={loading || aiTyping}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="sentences"
                  spellCheck="true"
                  style={{ 
                    lineHeight: '1.5', 
                    fontSize: '16px',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none'
                  }}
                />
                <button
                  onClick={processUserInput}
                  disabled={loading || aiTyping || !userInput.trim()}
                  className="bg-emerald-600 text-white p-2 md:px-4 md:py-2 rounded-full md:rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                >
                  <Send size={18} className="md:w-5 md:h-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InnovaFast;